import { useCurrentOrg, useSessionTokenGetter } from '@develio/auth/client';
import { WIZARD_STEPS, resolveEffectiveOverrides } from '@develio/shared';
import type {
  ProjectTypeOverrideBlock,
  ProjectTypeOverrides,
  ValidationBypass,
  ValidationWarning,
} from '@develio/shared';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { ValidationPanel } from '../../components/ValidationPanel';
import { AcknowledgementModal } from './AcknowledgementModal';
import { type ProfileCitation, WizardLockingProvider } from './WizardContext';
import { Step1ProjectDescription } from './steps/Step1ProjectDescription';
import { Step2CapitalInvestment } from './steps/Step2CapitalInvestment';
import { Step3Employment } from './steps/Step3Employment';
import { Step4Revenue } from './steps/Step4Revenue';
import { Step5Property } from './steps/Step5Property';
import { Step6Jurisdiction } from './steps/Step6Jurisdiction';
import { Step7Assumptions } from './steps/Step7Assumptions';
import { Step8Incentives } from './steps/Step8Incentives';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

// Steps 6 (Jurisdiction) and 7 (Assumptions) are removed from the main flow.
// Their values are pre-populated from the org's default profiles at mount and
// submitted as part of the form payload. Users can override them via the
// Advanced panel on the last step.
const VISIBLE_LABELS = ['Project', 'Capital', 'Employment', 'Revenue', 'Property', 'Incentives'];
const VISIBLE_COMPONENTS = [
  Step1ProjectDescription,
  Step2CapitalInvestment,
  Step3Employment,
  Step4Revenue,
  Step5Property,
  Step8Incentives,
] as const;
const VISIBLE_KEYS = ['step1', 'step2', 'step3', 'step4', 'step5', 'step8'] as const;
const VISIBLE_SCHEMAS = [
  WIZARD_STEPS[0],
  WIZARD_STEPS[1],
  WIZARD_STEPS[2],
  WIZARD_STEPS[3],
  WIZARD_STEPS[4],
  WIZARD_STEPS[7],
] as const;

/**
 * 8-step wizard for creating a new analysis.
 *
 * Uses a single React Hook Form instance over the full AnalysisInputSchema
 * so values persist across steps without re-mounting. Each step validates
 * its own slice via trigger() before allowing forward navigation.
 *
 * "Save as draft" is available from any step; "Run Analysis" runs on the
 * final step after all 8 steps pass full validation.
 */
export function WizardRoute() {
  const getToken = useSessionTokenGetter();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  // Set when Next is blocked by validation, so the user gets visible feedback
  // instead of a button that appears to do nothing.
  const [stepBlocked, setStepBlocked] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedUnlocked, setAdvancedUnlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitIssues, setSubmitIssues] = useState<Array<{ path: string; message: string }>>([]);
  // Trial user hit the 2-analysis cap on this run — offer to start the
  // subscription (charge now) for unlimited, instead of a dead-end error.
  const [trialLimitHit, setTrialLimitHit] = useState(false);
  const [startingSub, setStartingSub] = useState(false);
  const [startSubError, setStartSubError] = useState<string | null>(null);
  const [validationWarnings, setValidationWarnings] = useState<ValidationWarning[]>([]);
  const [validationBypasses, setValidationBypasses] = useState<ValidationBypass[]>([]);
  const [jurisdictionCitation, setJurisdictionCitation] = useState<ProfileCitation | undefined>();
  const [assumptionCitation, setAssumptionCitation] = useState<ProfileCitation | undefined>();
  // Task 1: cosmetic provenance of each prefilled step-6 field vs the state
  // seed. `default` = matches the seed, `custom` = org moved it.
  const [jurisdictionOrigins, setJurisdictionOrigins] = useState<
    Record<string, 'default' | 'custom'>
  >({});
  const [stateSeedLabel, setStateSeedLabel] = useState<string | undefined>();
  // ADR-030 Phase 3: retain the loaded assumption profile so projectType
  // changes can re-resolve the per-type override defaults.
  const [assumptionProfile, setAssumptionProfile] = useState<Record<string, unknown> | null>(null);
  const [ackOpen, setAckOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const currentOrg = useCurrentOrg();
  const canOverride = currentOrg?.role === 'owner' || currentOrg?.role === 'consultant';

  /** When "Edit & Re-run" is clicked on a result page, inputs are passed via router state. */
  const rerunInputs = (location.state as { rerunFrom?: unknown } | null)?.rerunFrom;

  // biome-ignore lint/suspicious/noExplicitAny: RHF generic conflicts with pnpm module resolution
  const form = useForm<any>({
    mode: 'onBlur',
    defaultValues: rerunInputs ?? {
      step1: {
        projectName: '',
        projectType: 'new_startup',
        industryDescription: '',
        naicsCode: '',
        city: '',
        county: '',
        state: 'TX',
        analysisPeriodYears: 10,
      },
      step2: {
        totalConstructionSpending: 0,
        land: 0,
        buildings: 0,
        machineryEquipment: 0,
        ffe: 0,
        inventory: 0,
        constructionDurationMonths: 12,
        baselineTotalConstructionSpending: 0,
        baselineLand: 0,
        baselineBuildings: 0,
        baselineMachineryEquipment: 0,
        baselineFfe: 0,
        baselineInventory: 0,
      },
      step3: {
        directJobs: 0,
        directAnnualPayroll: 0,
        payrollGrowthRate: 0.02,
        staffingRampYear1: 0.7,
        staffingRampYear2: 0.9,
        baselineDirectJobs: 0,
        baselineDirectAnnualPayroll: 0,
      },
      step4: {
        annualTaxableSales: 0,
        salesGrowthRate: 0.02,
        revenueRampYear1: 0.6,
        revenueRampYear2: 0.9,
        annualRoomRevenue: 0,
        annualMixedBeverageReceipts: 0,
        baselineAnnualTaxableSales: 0,
        baselineAnnualRoomRevenue: 0,
        baselineAnnualMixedBeverageReceipts: 0,
      },
      step5: {
        assessedRealProperty: 0,
        assessedBpp: 0,
        assessedInventory: 0,
        realPropertyAppreciationRate: 0.02,
        bppDepreciationRate: 0.1,
        inventoryGrowthRate: 0.02,
        baselineAssessedRealProperty: 0,
        baselineAssessedBpp: 0,
        baselineAssessedInventory: 0,
      },
      step6: {
        // Keep in sync with the canonical step-6 defaults in
        // @develio/shared analysis.ts (defaults-parity test guards the
        // shared/db/api copies).
        salesTaxRate: 0.02,
        propertyTaxRate: 0.005,
        hotelOccupancyTaxRate: 0.07,
        mixedBeverageShareRate: 0.016018,
        miscTaxesFeesPerWorker: 150,
        householdTaxableSpendingPerWorker: 12000,
        utilityFranchiseFeePerJob: 100,
        baseCityCostPerWorker: 1200,
        baseCountyCostPerWorker: 800,
        baseIsdCostPerStudent: 6000,
        costInflationRate: 0.03,
        // ADR-034 2.1: local wage tax. Default 0 / 1.0 preserves TX.
        localWageTaxRate: 0,
        residentTaxableShare: 1,
        // ADR-034: taxability + assessment. Defaults preserve TX (BPP +
        // inventory taxed, assessed == market).
        taxesBpp: true,
        taxesInventory: true,
        assessmentRatioReal: 1,
        assessmentRatioBpp: 1,
        assessmentRatioInventory: 1,
        // ADR-034 2.3: school recapture. Default 1.0 preserves TX.
        schoolLocalRetentionShare: 1,
        // ADR-036 tier presence. Default all tiers present (TX).
        hasCountyPropertyTax: true,
        hasSeparateSchoolDistrict: true,
      },
      step7: {
        operationsEmploymentMultiplier: 1.5,
        operationsEarningsMultiplier: 1.4,
        constructionEmploymentMultiplierJobsPerMillion: 8.5,
        constructionEarningsMultiplier: 0.6,
        constructionOutputMultiplier: 1.8,
        localConstructionCaptureRate: 0.5,
        workerLocalCaptureRate: 0.5,
        workerTaxableSpendingShare: 0.6,
        materialsShareOfConstruction: 0.5,
        materialsTaxabilityRate: 0.8,
        permitFeeRate: 0.005,
        displacementRate: 0.1,
        workerRelocationPct: 0.2,
        averageHouseholdSize: 2.5,
        schoolAgeChildrenPerHousehold: 0.5,
      },
      step8: {
        jobGrantPctOfPayroll: 0,
        jobGrantYears: 0,
        // ADR-035 3.3: grant base. Default payroll preserves TX behavior.
        jobGrantBase: 'payroll',
        effectiveWithholdingRate: 0,
        infrastructureGrantTotal: 0,
        infrastructureGrantYears: 0,
        propertyTaxAbatementPct: 0,
        propertyTaxAbatementYears: 0,
        // ADR-035 3.2: abatement base. Default increment = TX Ch. 312.
        abatementBase: 'increment',
        // ADR-035 3.1: PILOT / leaseback (cross-tier abatement). Default 0.
        pilotAbatementPct: 0,
        pilotAbatementYears: 0,
        // ADR-035 3.4: sales-tax rebate. Default 0 preserves TX.
        salesTaxRebatePct: 0,
        salesTaxRebateYears: 0,
        // ADR-035 simplified TIF (memo capture). Default 0.
        tifCaptureShare: 0,
        tifYears: 0,
        // ADR-035 3.5: AZ GPLET effective rate. null = off.
        gpletEffectivePropertyRate: null,
        // ADR-035 3.6: statutory approval gate (metadata).
        approvalGate: 'none',
        discountRate: 0.05,
      },
    },
  });

  // Pre-populate Step 1 jurisdiction labels and Step 6 rates from the org's
  // default jurisdiction profile. Skips if the user is editing a re-run
  // (rerunInputs already carries everything) or if the fields were edited
  // before the profile finished loading.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional mount-only effect; form + getToken are stable
  useEffect(() => {
    if (rerunInputs) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/api/v1/jurisdiction-profiles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const json = (await res.json()) as { profiles: Array<Record<string, unknown>> };
        const def = json.profiles.find((p) => p.isDefault) ?? json.profiles[0];
        if (!def || cancelled) return;

        const setIfEmpty = (path: string, val: unknown) => {
          if (val === null || val === undefined || val === '') return;
          const cur = form.getValues(path);
          if (cur === '' || cur === 0 || cur === undefined || cur === null) {
            form.setValue(path, val, { shouldDirty: false });
          }
        };

        setIfEmpty('step1.city', def.cityName);
        setIfEmpty('step1.county', def.countyName);
        setIfEmpty('step1.schoolDistrict', def.isdName);
        setIfEmpty('step1.state', def.stateName);
        setIfEmpty('step1.specialDistrict', def.specialDistrictName);

        const num = (v: unknown) => (v === null || v === undefined ? undefined : Number(v));
        setIfEmpty('step6.salesTaxRate', num(def.salesTaxRate));
        setIfEmpty('step6.propertyTaxRate', num(def.propertyTaxRate));
        setIfEmpty('step6.hotelOccupancyTaxRate', num(def.hotelOccupancyTaxRate));
        setIfEmpty('step6.mixedBeverageShareRate', num(def.mixedBeverageShareRate));
        setIfEmpty('step6.miscTaxesFeesPerWorker', num(def.miscTaxesFeesPerWorker));
        setIfEmpty(
          'step6.householdTaxableSpendingPerWorker',
          num(def.householdTaxableSpendingPerWorker),
        );
        setIfEmpty('step6.utilityFranchiseFeePerJob', num(def.utilityFranchiseFeePerJob));
        setIfEmpty('step6.baseCityCostPerWorker', num(def.baseCityCostPerWorker));
        setIfEmpty('step6.baseCountyCostPerWorker', num(def.baseCountyCostPerWorker));
        setIfEmpty('step6.baseIsdCostPerStudent', num(def.baseIsdCostPerStudent));
        setIfEmpty('step6.costInflationRate', num(def.costInflationRate));
        setIfEmpty('step6.localWageTaxRate', num(def.localWageTaxRate));
        setIfEmpty('step6.residentTaxableShare', num(def.residentTaxableShare));
        setIfEmpty('step6.countyPropertyTaxRate', num(def.countyPropertyTaxRate));
        setIfEmpty('step6.isdPropertyTaxRate', num(def.isdPropertyTaxRate));
        setIfEmpty('step6.specialDistrictName', def.specialDistrictName);
        setIfEmpty('step6.specialDistrictPropertyTaxRate', num(def.specialDistrictPropertyTaxRate));
        setIfEmpty('step6.specialDistrictCostPerWorker', num(def.specialDistrictCostPerWorker));

        // Citation shown on locked fields + acknowledgement modal.
        if (typeof def.name === 'string') {
          setJurisdictionCitation({
            name: def.name,
            lastEditedAt: typeof def.updatedAt === 'string' ? def.updatedAt : undefined,
          });
        }

        // Task 1: tag each prefilled step-6 field as matching the state seed
        // ("{postal} default") or customized. Compares the org default
        // profile's value against resolveJurisdictionDefaults for its state.
        // Purely cosmetic: failure just leaves the fields unchipped.
        const postal = typeof def.stateName === 'string' ? def.stateName.trim() : '';
        if (postal) {
          try {
            const sdRes = await fetch(
              `${API_URL}/api/v1/state-defaults/${encodeURIComponent(postal)}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (sdRes.ok && !cancelled) {
              const sd = (await sdRes.json()) as {
                postal: string;
                hasRecord: boolean;
                defaults: Record<string, string>;
                statutory?: {
                  taxesBpp: boolean;
                  taxesInventory: boolean;
                  assessmentRatioReal: number;
                  assessmentRatioBpp: number;
                  assessmentRatioInventory: number;
                  schoolLocalRetentionShare: number;
                  hasCountyPropertyTax: boolean;
                  hasSeparateSchoolDistrict: boolean;
                };
              };

              // ADR-034 2.2/2.4: taxability + assessment ratios are set by
              // state law, so prefill them straight from the State Record
              // (not the per-city profile). Direct set, not setIfEmpty: these
              // reflect the state, overriding the TX-preserving schema default.
              // The mount-only effect already returned early for re-runs, so
              // this never clobbers an edited snapshot.
              if (sd.statutory && !cancelled) {
                const s = sd.statutory;
                form.setValue('step6.taxesBpp', s.taxesBpp, { shouldDirty: false });
                form.setValue('step6.taxesInventory', s.taxesInventory, { shouldDirty: false });
                form.setValue('step6.assessmentRatioReal', s.assessmentRatioReal, {
                  shouldDirty: false,
                });
                form.setValue('step6.assessmentRatioBpp', s.assessmentRatioBpp, {
                  shouldDirty: false,
                });
                form.setValue('step6.assessmentRatioInventory', s.assessmentRatioInventory, {
                  shouldDirty: false,
                });
                form.setValue('step6.schoolLocalRetentionShare', s.schoolLocalRetentionShare, {
                  shouldDirty: false,
                });
                form.setValue('step6.hasCountyPropertyTax', s.hasCountyPropertyTax, {
                  shouldDirty: false,
                });
                form.setValue('step6.hasSeparateSchoolDistrict', s.hasSeparateSchoolDistrict, {
                  shouldDirty: false,
                });
              }
              // Seed-backed step-6 fields, by their shared bare key name.
              const SEED_KEYS = [
                'salesTaxRate',
                'propertyTaxRate',
                'hotelOccupancyTaxRate',
                'mixedBeverageShareRate',
                'miscTaxesFeesPerWorker',
                'householdTaxableSpendingPerWorker',
                'utilityFranchiseFeePerJob',
                'baseCityCostPerWorker',
                'baseCountyCostPerWorker',
                'baseIsdCostPerStudent',
                'countyPropertyTaxRate',
                'isdPropertyTaxRate',
                'costInflationRate',
              ] as const;
              const origins: Record<string, 'default' | 'custom'> = {};
              for (const key of SEED_KEYS) {
                const profileVal = def[key];
                const seedVal = sd.defaults?.[key];
                if (profileVal == null || seedVal == null) continue;
                const a = Number(profileVal);
                const b = Number(seedVal);
                if (!Number.isFinite(a) || !Number.isFinite(b)) continue;
                // Relative epsilon: rates (~0.02) and dollars (~12000) both compared safely.
                const matches = Math.abs(a - b) <= Math.max(1e-9, Math.abs(b) * 1e-9);
                origins[key] = matches ? 'default' : 'custom';
              }
              if (!cancelled) {
                setJurisdictionOrigins(origins);
                setStateSeedLabel(sd.hasRecord ? `${sd.postal} default` : 'US default');
              }
            }
          } catch {
            // Non-fatal: chips just won't render.
          }
        }
      } catch {
        // Non-fatal: user can still fill the form manually.
      }
    })();

    // Assumption profile (Step 5, 7, 8 governed fields) — ADR-029.
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/api/v1/assumption-profiles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const json = (await res.json()) as { profiles: Array<Record<string, unknown>> };
        const def = json.profiles.find((p) => p.isDefault) ?? json.profiles[0];
        if (!def || cancelled) return;

        setAssumptionProfile(def);

        const setIfEmpty = (path: string, val: unknown) => {
          if (val === null || val === undefined || val === '') return;
          const cur = form.getValues(path);
          if (cur === '' || cur === 0 || cur === undefined || cur === null) {
            form.setValue(path, val, { shouldDirty: false });
          }
        };
        const num = (v: unknown) => (v === null || v === undefined ? undefined : Number(v));

        // ADR-030 — resolve effective values for the currently-selected
        // projectType so the 7 overridable fields pre-fill with the correct
        // per-type defaults on initial mount. A second effect below re-applies
        // on subsequent projectType changes.
        const projectType = (form.getValues('step1.projectType') ?? 'new_startup') as
          | 'new_startup'
          | 'expansion'
          | 'retention';
        const baseBlock: ProjectTypeOverrideBlock = {
          displacementRate: num(def.displacementRate),
          workerRelocationPct: num(def.workerRelocationPct),
          staffingRampYear1: num(form.getValues('step3.staffingRampYear1')),
          staffingRampYear2: num(form.getValues('step3.staffingRampYear2')),
          revenueRampYear1: num(form.getValues('step4.revenueRampYear1')),
          revenueRampYear2: num(form.getValues('step4.revenueRampYear2')),
          realPropertyAppreciationRate: num(def.realPropertyAppreciationRate),
        };
        const effective = resolveEffectiveOverrides(
          baseBlock,
          (def.projectTypeOverrides as ProjectTypeOverrides | undefined) ?? null,
          projectType,
        );

        // Step 5
        setIfEmpty('step5.realPropertyAppreciationRate', effective.realPropertyAppreciationRate);
        setIfEmpty('step5.bppDepreciationRate', num(def.bppDepreciationRate));
        setIfEmpty('step5.inventoryGrowthRate', num(def.inventoryGrowthRate));
        // Step 3
        setIfEmpty('step3.payrollGrowthRate', num(def.payrollGrowthRate));
        // Step 4
        setIfEmpty('step4.salesGrowthRate', num(def.salesGrowthRate));
        // Step 7
        setIfEmpty('step7.operationsEmploymentMultiplier', num(def.operationsEmploymentMultiplier));
        setIfEmpty('step7.operationsEarningsMultiplier', num(def.operationsEarningsMultiplier));
        setIfEmpty(
          'step7.constructionEmploymentMultiplierJobsPerMillion',
          num(def.constructionEmploymentMultiplierJobsPerMillion),
        );
        setIfEmpty('step7.constructionEarningsMultiplier', num(def.constructionEarningsMultiplier));
        setIfEmpty('step7.constructionOutputMultiplier', num(def.constructionOutputMultiplier));
        setIfEmpty('step7.localConstructionCaptureRate', num(def.localConstructionCaptureRate));
        setIfEmpty('step7.workerLocalCaptureRate', num(def.workerLocalCaptureRate));
        setIfEmpty('step7.workerTaxableSpendingShare', num(def.workerTaxableSpendingShare));
        setIfEmpty('step7.materialsShareOfConstruction', num(def.materialsShareOfConstruction));
        setIfEmpty('step7.materialsTaxabilityRate', num(def.materialsTaxabilityRate));
        setIfEmpty('step7.permitFeeRate', num(def.permitFeeRate));
        setIfEmpty('step7.displacementRate', effective.displacementRate);
        setIfEmpty('step7.workerRelocationPct', effective.workerRelocationPct);
        // ADR-030 ramps — overridable per project type; set if override provided.
        setIfEmpty('step3.staffingRampYear1', effective.staffingRampYear1);
        setIfEmpty('step3.staffingRampYear2', effective.staffingRampYear2);
        setIfEmpty('step4.revenueRampYear1', effective.revenueRampYear1);
        setIfEmpty('step4.revenueRampYear2', effective.revenueRampYear2);
        setIfEmpty('step7.averageHouseholdSize', num(def.averageHouseholdSize));
        setIfEmpty('step7.schoolAgeChildrenPerHousehold', num(def.schoolAgeChildrenPerHousehold));
        // Step 8
        setIfEmpty('step8.discountRate', num(def.discountRate));

        if (typeof def.name === 'string') {
          setAssumptionCitation({
            name: def.name,
            lastEditedAt: typeof def.updatedAt === 'string' ? def.updatedAt : undefined,
          });
        }
      } catch {
        // Non-fatal: defaults from the wizard schema stand.
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ADR-030 Phase 3 — reactive project-type resolver.
   *
   * When the analyst picks a different project type on Step 1 *after* the
   * assumption profile has loaded, re-apply the 7 overridable fields with
   * the newly-resolved effective values. This overwrites the current values
   * unconditionally: these are methodology defaults, and the whole point of
   * Phase 3 is that "pick a project type → see the right defaults." Owners
   * who want to retain a custom typed value can re-enter it after the
   * project-type selection; for non-owners the fields are read-only anyway.
   *
   * The initial mount-effect above has already seeded the fields for the
   * current projectType, so we only re-apply when the value truly changes.
   */
  const watchedProjectType = form.watch('step1.projectType') as
    | 'new_startup'
    | 'expansion'
    | 'retention'
    | undefined;
  useEffect(() => {
    if (!assumptionProfile || !watchedProjectType) return;
    if (rerunInputs) return; // respect the re-run snapshot
    const num = (v: unknown) => (v === null || v === undefined ? undefined : Number(v));
    const def = assumptionProfile;
    const baseBlock: ProjectTypeOverrideBlock = {
      displacementRate: num(def.displacementRate),
      workerRelocationPct: num(def.workerRelocationPct),
      staffingRampYear1: num(form.getValues('step3.staffingRampYear1')),
      staffingRampYear2: num(form.getValues('step3.staffingRampYear2')),
      revenueRampYear1: num(form.getValues('step4.revenueRampYear1')),
      revenueRampYear2: num(form.getValues('step4.revenueRampYear2')),
      realPropertyAppreciationRate: num(def.realPropertyAppreciationRate),
    };
    const effective = resolveEffectiveOverrides(
      baseBlock,
      (def.projectTypeOverrides as ProjectTypeOverrides | undefined) ?? null,
      watchedProjectType,
    );
    const apply = (path: string, val: number | undefined) => {
      if (val === undefined || !Number.isFinite(val)) return;
      form.setValue(path, val, { shouldDirty: false });
    };
    apply('step5.realPropertyAppreciationRate', effective.realPropertyAppreciationRate);
    apply('step7.displacementRate', effective.displacementRate);
    apply('step7.workerRelocationPct', effective.workerRelocationPct);
    apply('step3.staffingRampYear1', effective.staffingRampYear1);
    apply('step3.staffingRampYear2', effective.staffingRampYear2);
    apply('step4.revenueRampYear1', effective.revenueRampYear1);
    apply('step4.revenueRampYear2', effective.revenueRampYear2);
    // `form` is stable across renders; `rerunInputs` is only read for the early-return.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedProjectType, assumptionProfile]);

  /**
   * ADR-031 — seed retention counterfactual defaults when retention is
   * first selected. Conservative defaults (P=0.5, departure year 2, zero
   * retained share, 25% local absorption mirroring ADR-030's displacement
   * default) require the analyst to consciously move each knob. Leaves
   * existing values alone on subsequent selection cycles (re-run / edit).
   *
   * Clears the block when moving back to new_startup/expansion so a stale
   * retention CF doesn't ride along on a non-retention payload.
   */
  useEffect(() => {
    if (rerunInputs) return; // respect re-run snapshot
    if (watchedProjectType === 'retention') {
      const existing = form.getValues('retentionCounterfactual');
      if (existing == null) {
        // ADR-031 Part D — seed P(departure) from the assumption profile's
        // retention override if available; otherwise fall back to 0.5.
        const overrides = assumptionProfile?.projectTypeOverrides as
          | ProjectTypeOverrides
          | undefined;
        const profileP = overrides?.retention?.defaultProbabilityOfDeparture;
        form.setValue(
          'retentionCounterfactual',
          {
            probabilityOfDeparture: typeof profileP === 'number' ? profileP : 0.5,
            departureTimingYear: 2,
            retainedSharePostDeparture: 0,
            localAbsorptionRate: 0.25,
            evidence: '',
          },
          { shouldDirty: false },
        );
      }
    } else if (watchedProjectType === 'new_startup' || watchedProjectType === 'expansion') {
      if (form.getValues('retentionCounterfactual') != null) {
        form.setValue('retentionCounterfactual', undefined, { shouldDirty: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedProjectType, assumptionProfile]);

  // biome-ignore lint/style/noNonNullAssertion: step is always within VISIBLE_COMPONENTS bounds
  const StepComponent = VISIBLE_COMPONENTS[step]!;
  const isLastStep = step === VISIBLE_LABELS.length - 1;

  const validateCurrentStep = useCallback(async () => {
    const stepSchema = VISIBLE_SCHEMAS[step];
    const key = VISIBLE_KEYS[step];
    if (!stepSchema || !key) return true;
    const currentValues = form.getValues(key);
    const result = stepSchema.safeParse(currentValues);
    if (!result.success) {
      await form.trigger(key as Parameters<typeof form.trigger>[0]);
      return false;
    }
    return true;
  }, [form, step]);

  /**
   * Call POST /api/v1/analyses/validate with the current step 3+4 values
   * and update the warning list. Fires after steps 3 and 4.
   */
  const runBenchmarkValidation = useCallback(async () => {
    try {
      const token = await getToken();
      const values = form.getValues();
      const body = {
        naicsCode: values.step1?.naicsCode,
        directJobs: values.step3?.directJobs,
        directAnnualPayroll: values.step3?.directAnnualPayroll,
        annualTaxableSales: values.step4?.annualTaxableSales,
        annualRoomRevenue: values.step4?.annualRoomRevenue,
        totalConstructionSpending: values.step2?.totalConstructionSpending,
      };
      const res = await fetch(`${API_URL}/api/v1/analyses/validate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = (await res.json()) as { warnings: ValidationWarning[] };
        setValidationWarnings(data.warnings ?? []);
        // Clear bypasses that no longer correspond to an active warning
        setValidationBypasses((prev) =>
          prev.filter((b) => data.warnings.some((w) => w.field === b.field)),
        );
      }
    } catch {
      // Non-fatal: validation is best-effort
    }
  }, [getToken, form]);

  const handleNext = useCallback(async () => {
    const valid = await validateCurrentStep();
    if (!valid) {
      setStepBlocked(true);
      return;
    }
    setStepBlocked(false);
    if (step < VISIBLE_LABELS.length - 1) {
      // After Employment (index 2) or Revenue (index 3), run benchmarks
      if (step === 2 || step === 3) {
        void runBenchmarkValidation();
      }
      setStep((s) => s + 1);
    }
  }, [validateCurrentStep, step, runBenchmarkValidation]);

  const handleBack = useCallback(() => {
    setStepBlocked(false);
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  const handleSaveDraft = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const token = await getToken();
      const values = form.getValues();
      const res = await fetch(`${API_URL}/api/v1/analyses`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectName: values.step1.projectName || 'Untitled Analysis',
          status: 'draft',
          inputs: values,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `Save failed (${res.status})`);
      }
      navigate('/analyses');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }, [getToken, form, navigate]);

  const handleSubmit = form.handleSubmit(async (values) => {
    setSubmitting(true);
    setSubmitError(null);
    setSubmitIssues([]);
    try {
      const token = await getToken();
      const acknowledgement = {
        acknowledgedAt: new Date().toISOString(),
        profileNames: {
          jurisdiction: jurisdictionCitation?.name ?? null,
          assumption: assumptionCitation?.name ?? null,
        },
      };
      const payload: Record<string, unknown> = { ...values, acknowledgement };
      if (validationBypasses.length > 0) payload.validationBypasses = validationBypasses;
      if (canOverride && overrideReason.trim()) {
        payload.overrideReason = overrideReason.trim();
      }
      const res = await fetch(`${API_URL}/api/v1/analyses/calculate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          issues?: Array<{ path: Array<string | number> | string; message: string }>;
        };
        // Trial cap reached: surface the start-subscription prompt instead of a
        // generic error so the user can unlock unlimited and continue.
        if (body.code === 'TRIAL_ANALYSIS_LIMIT_REACHED') {
          setTrialLimitHit(true);
          return;
        }
        // No subscription at all (e.g. deep link before starting the trial):
        // send them to the billing wall.
        if (body.code === 'SUBSCRIPTION_REQUIRED') {
          navigate('/start-trial');
          return;
        }
        if (Array.isArray(body.issues) && body.issues.length > 0) {
          setSubmitIssues(
            body.issues.map((i) => ({
              path: Array.isArray(i.path) ? i.path.join('.') : String(i.path ?? ''),
              message: i.message,
            })),
          );
        }
        throw new Error(body.error ?? `Calculation failed (${res.status})`);
      }
      const result = await res.json();
      navigate(`/analyses/${result.analysisId as string}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Calculation failed');
    } finally {
      setSubmitting(false);
    }
  });

  // Start the subscription now (ends the trial early, charges the card on
  // file), then re-run the analysis the user was blocked on.
  const startSubscription = useCallback(async () => {
    setStartingSub(true);
    setStartSubError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}/api/v1/billing/end-trial`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const b = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(b.error ?? 'Could not start your subscription. Please try again.');
      }
      setTrialLimitHit(false);
      // Org is now on a paid (unlimited) plan — re-run the blocked analysis.
      void handleSubmit();
    } catch (err) {
      setStartSubError(
        err instanceof Error ? err.message : 'Could not start your subscription. Please try again.',
      );
    } finally {
      setStartingSub(false);
    }
  }, [getToken, handleSubmit]);

  // The pre-2026-05 build hard-gated the wizard behind a 5-step setup
  // checklist. With sample profiles seeded on signup, the /onboarding
  // setup flow, and the national RIMS fallback, that gate is no longer
  // required — users can run an analysis on estimated data and refine
  // later. The dashboard banner + PDF watermark still warn about sample
  // data. If a brand-new org somehow lands here directly, the redirect
  // in ProtectedShell sends them to /onboarding first.

  return (
    <WizardLockingProvider
      jurisdictionCitation={jurisdictionCitation}
      assumptionCitation={assumptionCitation}
    >
      {/* Trial cap reached — start-subscription prompt (charge now → unlimited) */}
      {trialLimitHit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">
              You've reached your 2 trial analyses
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Start your subscription to unlock unlimited analyses. Your card on file will be
              charged now, and your plan renews automatically until you cancel.
            </p>
            {startSubError && (
              <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {startSubError}
              </p>
            )}
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setTrialLimitHit(false)}
                disabled={startingSub}
                className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 disabled:opacity-60"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={startSubscription}
                disabled={startingSub}
                className="flex-1 rounded-lg bg-brand-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {startingSub ? 'Starting…' : 'Start subscription'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-3xl space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {rerunInputs ? 'Edit & Re-run' : 'New Analysis'}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Step {step + 1} of {VISIBLE_LABELS.length} — {VISIBLE_LABELS[step]}
          </p>
        </div>

        {/* Step progress tabs */}
        <nav aria-label="Wizard steps" className="flex gap-1">
          {VISIBLE_LABELS.map((label, i) => (
            <button
              key={label}
              type="button"
              onClick={() => {
                setStepBlocked(false);
                setStep(i);
              }}
              className={`flex-1 truncate rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                i === step
                  ? 'bg-slate-900 text-white'
                  : i < step
                    ? 'bg-slate-200 text-slate-600'
                    : 'bg-slate-100 text-slate-400'
              }`}
            >
              {i + 1}. {label}
            </button>
          ))}
        </nav>

        {/* Step form card */}
        <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (isLastStep) void handleSubmit(e);
            }}
          >
            <StepComponent form={form} />
          </form>
        </div>

        {/* Advanced override panel — jurisdiction & methodology, intentionally tucked away */}
        {isLastStep && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 shadow-sm">
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="flex w-full items-center justify-between px-6 py-4 text-left"
            >
              <span className="text-sm font-medium text-slate-500">
                Advanced: Jurisdiction &amp; Methodology Assumptions
              </span>
              <span className="text-xs text-slate-400">{advancedOpen ? '▲ Hide' : '▼ Show'}</span>
            </button>
            {advancedOpen && (
              <div className="space-y-6 border-t border-slate-200 px-6 pb-6 pt-4">
                {!advancedUnlocked ? (
                  <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <p className="text-sm text-amber-800">
                      <strong>EDC Standards are in use.</strong> The jurisdiction rates and
                      methodology assumptions below are pre-set from your organization's EDC
                      standards. Modifying them may affect analysis comparability and should only be
                      done with administrator approval.
                    </p>
                    <button
                      type="button"
                      onClick={() => setAdvancedUnlocked(true)}
                      className="rounded-md border border-amber-400 bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-200"
                    >
                      I understand — let me adjust these values
                    </button>
                  </div>
                ) : (
                  <div className="space-y-8">
                    <div>
                      <h3 className="mb-4 text-sm font-semibold text-slate-700">
                        Jurisdiction Rates
                      </h3>
                      <Step6Jurisdiction
                        form={form}
                        origins={jurisdictionOrigins}
                        seedLabel={stateSeedLabel}
                      />
                    </div>
                    <div>
                      <h3 className="mb-4 text-sm font-semibold text-slate-700">
                        Methodology Assumptions
                      </h3>
                      <Step7Assumptions form={form} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Benchmark validation panel — shown on steps 4–6 when warnings exist */}
        {step >= 3 && validationWarnings.length > 0 && (
          <ValidationPanel
            warnings={validationWarnings}
            bypasses={validationBypasses}
            onBypassChange={setValidationBypasses}
          />
        )}

        {/* Error message */}
        {submitError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <div className="font-medium">{submitError}</div>
            {submitIssues.length > 0 && (
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                {submitIssues.map((issue, idx) => (
                  <li key={`${issue.path}-${idx}`}>
                    {issue.path ? (
                      <>
                        <span className="font-mono font-medium">{issue.path}</span>
                        {' — '}
                        {issue.message}
                      </>
                    ) : (
                      issue.message
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Validation feedback — shown when Next is blocked, so the button
            never looks like it silently failed. */}
        {stepBlocked && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Please complete the highlighted fields on this step before continuing.
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleBack}
              disabled={step === 0}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-40 hover:bg-slate-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => void handleSaveDraft()}
              disabled={submitting}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-500 disabled:opacity-40 hover:bg-slate-50"
            >
              Save draft
            </button>
          </div>

          {isLastStep ? (
            <button
              type="button"
              onClick={() => setAckOpen(true)}
              disabled={submitting}
              className="rounded-md bg-slate-900 px-6 py-2 text-sm font-medium text-white disabled:opacity-60 hover:bg-slate-700"
            >
              {submitting ? 'Running…' : 'Run Analysis'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handleNext()}
              className="rounded-md bg-slate-900 px-6 py-2 text-sm font-medium text-white hover:bg-slate-700"
            >
              Next
            </button>
          )}
        </div>

        <AcknowledgementModal
          open={ackOpen}
          submitting={submitting}
          jurisdictionCitation={jurisdictionCitation}
          assumptionCitation={assumptionCitation}
          canOverride={canOverride}
          overrideReason={overrideReason}
          onOverrideReasonChange={setOverrideReason}
          onCancel={() => setAckOpen(false)}
          onConfirm={() => {
            setAckOpen(false);
            void handleSubmit();
          }}
        />
      </div>
    </WizardLockingProvider>
  );
}
